import React, { useState, useEffect, useCallback } from 'react';
import { Search } from 'carbon-components-react';
import Util from 'utilities/helper.util';
import { useSaga } from 'use-reducer-saga';
import { addPin, removePin } from 'components/Map/helper';
import MapSettings from 'components/Map/settings';
import reducer, { initialState } from './localStore/reducer';
import sagas from './localStore/sagas';
import {
  getAddressSuggestStart,
  clearAddressSuggest,
  getAddressCandidateStart,
  clearAddressCandidate
} from './localStore/actions';
import { useEmptyCallback } from 'utilities/hooks.util';
import ResultsBox from './resultBox';
import { arcGISWorldGeocodingLabelTrim } from './helper';

const searchMapZoomLevel = MapSettings.zoomLevel;

const AddressSearch: React.FC<Props> = ({ onClose }) => {
  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const [localStore, localDispatch] = useSaga(reducer, initialState, sagas);
  const [isShowResult, setIsShowResult] = useState<boolean>(false);

  const { suggestion, addressCandidate } = localStore;

  const debouncedFunction = useEmptyCallback(
    Util.debounce(async (args: string[]) => {
      const text = args[0];
      const location = args[1];
      const payload = {
        text,
        limit: '5',
        location
      };
      localDispatch(getAddressSuggestStart(payload));
    }, 250)
  );

  const clearSuggestions = useEmptyCallback(() => {
    localDispatch(clearAddressSuggest());
  });

  const clearCandidate = useEmptyCallback(() => {
    localDispatch(clearAddressCandidate());
  });

  const handleInputChange = (value: string) => {
    if (value) {
      debouncedFunction(
        value,
        `${window.loadedView.center.longitude},${window.loadedView.center.latitude}`
      );
    } else {
      clearSuggestions();
    }
    setSearchInputValue(value);
  };

  const onClickResult = useEmptyCallback((item: any) => {
    localDispatch(
      getAddressCandidateStart({
        text: item.text,
        outFields: '*',
        magicKey: item.magicKey
      })
    );
  });

  const processAddressCandidate = useCallback(
    (addressCandidate) => {
      const { loadedView } = window;
      const addrCandidate = addressCandidate?.[0];

      if (addrCandidate) {
        const addrCandidateAttr = addrCandidate.attributes;
        if (loadedView) {
          removePin();
          const center = [addrCandidate.location.x, addrCandidate.location.y];
          if (addrCandidateAttr.Addr_type !== 'Locality') {
            addPin(
              center,
              arcGISWorldGeocodingLabelTrim(addrCandidate.address, true)
            );
          }
          loadedView.goTo({ center, zoom: searchMapZoomLevel - 1 });
        }
        setSearchInputValue(
          arcGISWorldGeocodingLabelTrim(addrCandidate.address, true)
        );
      }

      clearCandidate();
      onClose();
    },
    [clearCandidate, onClose]
  );

  useEffect(() => {
    setIsShowResult(suggestion.length > 0);
  }, [suggestion]);

  useEffect(() => {
    if (addressCandidate) {
      processAddressCandidate(addressCandidate);
    }
  }, [addressCandidate, processAddressCandidate]);

  return (
    <>
      <label className="bx--label">ADDRESS SEARCH</label>
      <Search
        id="address-input"
        size="xl"
        labelText="ADDRESS SEARCH"
        value={searchInputValue}
        onChange={(evt) => handleInputChange(evt.target.value)}
      />
      {isShowResult && (
        <div className="mls-item-container">
          <ResultsBox
            items={suggestion}
            onItemClick={onClickResult}
            itemToLabel={(item: any) =>
              arcGISWorldGeocodingLabelTrim(item.text)
            }
          />
        </div>
      )}
    </>
  );
};

type Props = {
  onClose: Function;
};

export default React.memo(AddressSearch);
